import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { LATERALITY_CODES, type Laterality } from '@/data/imageTypes';

/**
 * The laterality as three tiles, Left · Bilateral · Right, each a paired
 * organ (two lobes side by side, the side it says filled and the other
 * outlined — both filled for bilateral) and its name. The row is outlined as
 * one control, no wider than its tiles. The chosen tile is filled with the
 * primary colour, its glyph inverted; clicking it again clears the field, so
 * Laterality is un-said without a fourth "none" tile. A radiogroup of
 * `aria-checked` tiles: arrows move between them, Space and Enter toggle.
 * Sized to sit beside the forms' inputs: `sm` is 32px tall (enterprise), `md`
 * 36 (community).
 */
const TILES = ['L', 'B', 'R'] as const satisfies readonly Laterality[];

/** One lobe of the pair: a pill, 6 wide and 14 tall, at `x`. */
function Lobe({ x, filled }: { x: number; filled: boolean }) {
  return <rect x={x} y={3} width={6} height={14} rx={3} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.5} />;
}

/** The paired organ with the side it says filled (both for bilateral), in `currentColor`. */
export function LateralityGlyph({ side, size = 16 }: { side: Laterality; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden className="shrink-0">
      <Lobe x={2.5} filled={side !== 'R'} />
      <Lobe x={11.5} filled={side !== 'L'} />
    </svg>
  );
}

export function LateralityPicker({
  value,
  onChange,
  disabled = false,
  size = 'sm',
  label = 'Laterality',
  className,
}: {
  value: Laterality | null;
  onChange: (v: Laterality | null) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** What the group is, for assistive technology. */
  label?: string;
  className?: string;
}) {
  const tiles = useRef<Array<HTMLButtonElement | null>>([]);
  // Arrows move between the tiles; Tab lands on the chosen one (the first when none is).
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    tiles.current[(i + step + TILES.length) % TILES.length]?.focus();
  };
  const chosen = value && TILES.includes(value) ? value : null;
  return (
    <div className={cn('inline-flex self-start', disabled && 'opacity-50', className)}>
      <div role="radiogroup" aria-label={label} aria-disabled={disabled || undefined} className="inline-flex overflow-hidden rounded-md border border-input bg-background">
        {TILES.map((l, i) => {
          const on = l === chosen;
          const name = LATERALITY_CODES[l].meaning;
          return (
            <button
              key={l}
              ref={(el) => {
                tiles.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={l === (chosen ?? TILES[0]) ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(on ? null : l)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap border-input font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed',
                i > 0 && 'border-l',
                size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3 text-sm',
                on ? 'bg-primary text-primary-foreground' : cn('bg-background text-muted-foreground', !disabled && 'hover:bg-accent hover:text-foreground'),
              )}
            >
              <LateralityGlyph side={l} />
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
